import { type FlagName, useFlagSnapshot } from '@platform/feature-flags';
import type { IconName } from '@platform/ui';
import type { AnyRoute } from '@tanstack/react-router';
import type { AbsolutePath } from './definePath';

export type Surface =
  | 'home-privacy'
  | 'home-account'
  | 'home-network-actions'
  | 'personal-data'
  | 'notifications-actions'
  | 'invoices-actions'
  | 'payment-change-actions'
  | 'device-actions';

const DEFAULT_QUICK_ACTION_ORDER = 100;

export interface QuickActionMeta {
  /** Path used by the Hub `<Link>` — usually a `*Paths.<x>.path` constant from the same domain. */
  to: AbsolutePath;
  icon: IconName;
  title: () => string;
  subline: () => string;
  surfaces: readonly Surface[];
  /** Sort weight; lower = earlier. Default {@link DEFAULT_QUICK_ACTION_ORDER}. */
  order?: number;
}

export interface QuickAction extends QuickActionMeta {
  /** Resolved from the route's `staticData.featureFlag`. When set, the action is hidden while the flag is off. */
  flagName?: FlagName;
}

let registry: readonly QuickAction[] = [];

export function collectQuickActions(routes: readonly AnyRoute[]): QuickAction[] {
  const collected: QuickAction[] = [];
  function walk(route: AnyRoute) {
    const meta = route.options.staticData?.quickAction;
    if (meta) {
      const flagName = route.options.staticData?.featureFlag;
      collected.push({
        ...meta,
        ...(flagName && { flagName }),
      });
    }
    for (const child of route.children ?? []) {
      walk(child);
    }
  }
  for (const route of routes) {
    walk(route);
  }
  collected.sort((a, b) => (a.order ?? DEFAULT_QUICK_ACTION_ORDER) - (b.order ?? DEFAULT_QUICK_ACTION_ORDER));
  return collected;
}

export function registerQuickActions(actions: readonly QuickAction[]): void {
  registry = actions;
}

export function useQuickActions(surface: Surface): readonly QuickAction[] {
  const flags = useFlagSnapshot();
  return registry.filter((qa) => qa.surfaces.includes(surface) && (!qa.flagName || flags[qa.flagName] === true));
}

/** Adapter from a registry entry to the `{ kind: 'internal', ... }` shape used by list-style action consumers. */
export function toInternalActionItem(qa: QuickAction) {
  return {
    kind: 'internal' as const,
    order: qa.order ?? DEFAULT_QUICK_ACTION_ORDER,
    title: qa.title,
    subline: qa.subline,
    icon: qa.icon,
    to: qa.to,
  };
}
